import { css } from '@emotion/react';
import { AnchorLink } from 'gatsby-plugin-anchor-links';
import { ButtonLink } from '@/components/shared/ButtonLink';
import { PRIVACY_POLICY_URL } from '@/constants/url';
import { pagesPath } from '@/lib/pagesPath';
import { getScreen } from '@/styles/media-query';
import { withPrefix } from 'gatsby';

export type ContactSectionProps = {
  className?: string;
  title1: React.ReactNode;
  title2: React.ReactNode;
  title3: React.ReactNode;
  title4: React.ReactNode;
  text?: string;
  rounded?: boolean;
  button?: boolean;
};

export const ContactSection: React.FC<ContactSectionProps> = ({
  className,
  title1,
  title2,
  title3,
  title4,
  text,
  rounded,
  button,
}) => {
  return (
    <section
      css={css`
        text-align: center;
        color: #ffffff;
        padding: 43px 30px 30px;
        position: relative;
        z-index: 1;
        pointer-events: none;

        ${rounded &&
        css`
          padding-top: 96px;
        `}

        @media ${getScreen('pc')} {
          padding: 75px 30px 80px;

          ${rounded &&
          css`
            padding-top: 236px;
          `}
        }
      `}
      className={className}
    >
      {/* 背景装飾・丸み */}
      <div
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          z-index: -1;
          width: 100%;
          height: 100%;
          pointer-events: none;
          background: url(${withPrefix('/images/contact-bg.png')}) center bottom -150px/450px 450px
              no-repeat,
            linear-gradient(345deg, #00b3a7 0%, #71d9d2cc 100%) 0% 0% no-repeat;

          ${rounded &&
          css`
            mask: url(${withPrefix('/images/section-round-sp.png')}) center top/100% 64px
                no-repeat,
              linear-gradient(0deg, #000 0%, #000 100%) center 63px/100% 100%
                no-repeat;
          `}

          @media ${getScreen('pc')} {
            background: url(${withPrefix('/images/contact-bg-pc.png')}) center top/2000px 800px
                no-repeat,
              linear-gradient(304deg, #00b3a7 0%, #71d9d2cc 100%) 0% 0%
                no-repeat;

            ${rounded &&
            css`
              mask: url(${withPrefix('/images/section-round-pc.png')}) center top/100% 160px
                  no-repeat,
                linear-gradient(0deg, #000 0%, #000 100%) center 159px/100% 100%
                  no-repeat;
            `}
          }
        `}
      ></div>
      <div
        css={css`
          pointer-events: all;
        `}
      >
        <p
          css={css`
            margin: 0 -5px;

            @media ${getScreen('pc')} {
              margin: 0;
            }
          `}
        >
          <span
            css={css`
              display: inline;
              font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.56px;
              color: #ffffff;
              text-shadow: 0px 0px 1px #0e3e8129;
              margin-bottom: 2px;

              @media ${getScreen('pc')} {
                font: normal normal bold 22px/32px 'Noto Sans JP', sans-serif;
                margin-bottom: 5px;
              }
            `}
          >
            {title1}
          </span>
          <br />
          <strong
            css={css`
              display: inline;
              font: normal normal bold 28px/40px 'Noto Sans JP', sans-serif;
              letter-spacing: 1.12px;
              color: #ffffff;
              text-shadow: 0px 0px 1px #00141233;

              @media ${getScreen('pc')} {
                font: normal normal bold 40px/58px 'Noto Sans JP', sans-serif;
              }
            `}
          >
            {title2}
          </strong>
          <span
            css={css`
              display: inline;
              font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.56px;
              color: #ffffff;
              text-shadow: 0px 0px 3px #0014121c;

              @media ${getScreen('pc')} {
                font: normal normal bold 20px/29px 'Noto Sans JP', sans-serif;
              }
            `}
          >
            {title3}
          </span>
          <br />
          <span
            css={css`
              margin-top: 2px;
              display: inline;
              font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.56px;
              color: #ffffff;
              text-shadow: 0px 0px 6px #0014121c;

              @media ${getScreen('pc')} {
                font: normal normal bold 20px/29px 'Noto Sans JP', sans-serif;
                margin-bottom: 10px;
              }
            `}
          >
            {title4}
          </span>
        </p>
        {text && (
          <p
            css={css`
              font: normal normal bold 20px/29px 'Noto Sans JP', sans-serif;
              letter-spacing: 0.8px;
              color: #ffffff;
              text-shadow: 0px 0px 1px #0e3e8129;
              margin-top: 32px;

              @media ${getScreen('pc', '未満')} {
                display: none;
              }
            `}
          >
            {text}
          </p>
        )}
      {!button && (
        <div
          css={css`
            margin: 12px auto 9px;

            @media ${getScreen('pc')} {
              display: none;
            }
          `}
        >
          <img
            src={withPrefix('/images/contact-image.png')}
            width="316"
            height="207"
            alt=""
          />
        </div>
      )}
      {!button && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;

            @media ${getScreen('pc')} {
              flex-direction: row;
              justify-content: center;
              margin-top: 26px;
            }
          `}
        >
          <ButtonLink
            css={css`
              @media ${getScreen('pc')} {
                width: 188px;
              }
            `}
            to={pagesPath.contact.$url()}
            variant="contact-demo"
          >
            相談する
          </ButtonLink>
          <ButtonLink
            css={css`
              @media ${getScreen('pc')} {
                width: 188px;
              }
            `}
            to={pagesPath.download.$url()}
            variant="contact-download"
          >
            資料請求
          </ButtonLink>
        </div>
      )}
      {!button && (
        <p
          css={css`
            text-align: center;
            font: normal normal 500 12px/20px 'Noto Sans JP', sans-serif;
            letter-spacing: 0.24px;
            color: #ffffff;
            margin: 15px -10px 0;

            @media ${getScreen('pc')} {
              display: none;
            }

            a {
              text-decoration: underline;
            }
          `}
        >
          Uniforceの
          <AnchorLink to={pagesPath.rule.$url()}>利用規約</AnchorLink>、
          <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
            プライバシーポリシー
          </a>
          同意の上で
          <br />
          ダウンロードページにお進みください。
        </p>
      )}
      </div>
    </section>
  );
};
